/**
 * 言語コンテキスト
 *
 * @file LanguageContext.tsx
 * @version 1.0.0
 * @author Japan4U Development Team
 * @summary
 * アプリケーション全体で言語設定とi18next初期化を管理するContext。
 * 動的namespace解決システムを統合し、簡素でスケーラブルな翻訳システムを提供。
 */

import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import i18n, { ensureNamespacesLoaded, registerNamespaces as i18nRegisterNamespaces, getLoadedNamespaces, getRegisteredNamespaces } from '../i18n';

export type TranslationFiles = string[];

/**
 * URLパスから言語コードを検出する
 * 例: /en/products → 'en', /ko/cart → 'ko', /products → null
 */
const detectLanguageFromPath = (pathname: string): string | null => {
  const match = pathname.match(/^\/(ja|ko|en|zh)(\/|$)/);
  return match ? match[1] : null;
};

const normalizeLanguage = (lng: string): string => lng.split('-')[0];

interface LanguageContextType {
  currentLanguage: string;
  changeLanguage: (lang: string) => void;
  isReady: boolean;
  t: (key: string, options?: any) => string;
  registerNamespaces: (namespaces: string[]) => Promise<void>;
  loadedNamespaces: string[];
  registeredNamespaces: string[];
  getLocalizedText: <T extends Record<string, string | undefined>>(
    multiLangObj: T | undefined,
    fallback?: string
  ) => string;
  getLocalizedString: (
    value: string | Record<string, string | undefined> | null | undefined,
    fallback?: string
  ) => string;
  getLocaleCode: () => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// 共通Loading画面コンポーネント
export const I18nLoadingScreen: React.FC = () => (
  <div className="i18n-loading-container" style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 'var(--app-height)',
    fontSize: '18px',
    color: 'var(--text-secondary-gray)',
    backgroundColor: 'var(--bg-light-gray)'
  }}>
    <div style={{
      textAlign: 'center',
      padding: '40px'
    }}>
      <div style={{
        width: '40px',
        height: '40px',
        border: '4px solid #f3f3f3',
        borderTop: '4px solid var(--brand-main-indigo)',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite' as any,
        margin: '0 auto 20px'
      }}></div>
      <div>システム初期化中...</div>
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      ` }} />
    </div>
  </div>
);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    // 初期値をi18nの現在の言語から取得
    return normalizeLanguage(i18n.language);
  });
  const [isI18nReady, setIsI18nReady] = useState(false);
  const [updateTrigger, setUpdateTrigger] = useState(0);

  // i18next初期化処理
  useEffect(() => {
    const checkI18nReady = () => {
      if (i18n.isInitialized) {
        // i18next初期化完了後にコンテキストを利用可能にする
        setIsI18nReady(true);
        // i18nの現在の言語をcurrentLanguageに同期
        setCurrentLanguage(normalizeLanguage(i18n.language));
      }
    };

    const handleResourcesLoaded = () => {
      // 翻訳リソースロード完了時の強制再レンダリング
      console.log('[LanguageContext] Resources loaded, triggering re-render:', updateTrigger);
      setUpdateTrigger(prev => prev + 1);
    };

    // 既に初期化済みの場合は即座にReady状態にして再レンダリングをトリガー
    if (i18n.isInitialized) {
      setIsI18nReady(true);
      // i18nの現在の言語をcurrentLanguageに同期
      setCurrentLanguage(normalizeLanguage(i18n.language));
      // 初期化済みの場合も再レンダリングは自動的にトリガーされる
    }

    i18n.on('initialized', checkI18nReady);
    i18n.on('loaded', handleResourcesLoaded);
    i18n.on('added', handleResourcesLoaded);
    i18n.on('languageChanged', handleResourcesLoaded);

    return () => {
      i18n.off('initialized', checkI18nReady);
      i18n.off('loaded', handleResourcesLoaded);
      i18n.off('added', handleResourcesLoaded);
      i18n.off('languageChanged', handleResourcesLoaded);
    };
  }, []);

  const changeLanguageGenerationRef = useRef(0);

  const changeLanguage = useCallback(async (lang: string) => {
    const generation = ++changeLanguageGenerationRef.current;
    try {
      await i18n.changeLanguage(lang);
      if (generation !== changeLanguageGenerationRef.current) {
        // より新しいchangeLanguage呼び出しが発生済みのため、この結果は破棄する
        return;
      }
      setCurrentLanguage(normalizeLanguage(lang));
      document.documentElement.setAttribute('lang', lang);
    } catch (error) {
      console.error('Language change failed:', error);
    }
  }, []);

  // URLパスからの言語検出（初回マウント時のみ実行）
  const hasDetectedFromPath = useRef(false);
  useEffect(() => {
    if (hasDetectedFromPath.current) {
      return;
    }
    if (typeof window !== 'undefined' && isI18nReady) {
      const pathLang = detectLanguageFromPath(window.location.pathname);
      if (pathLang && pathLang !== currentLanguage) {
        // URLパスに言語が含まれている場合、その言語に切り替え
        console.log('[LanguageContext] Detected language from URL path:', pathLang);
        changeLanguage(pathLang);
      }
      hasDetectedFromPath.current = true;
    }
  }, [isI18nReady, currentLanguage, changeLanguage]);

  // 簡素化されたnamespaceロード関数
  const loadNamespaces = async (namespaces: string[]): Promise<void> => {
    try {
      await ensureNamespacesLoaded(namespaces);

      // タイミング競合を防ぐための待機処理
      await new Promise(resolve => setTimeout(resolve, 50));

      // 再レンダリングは自動的にトリガーされる
    } catch (error) {
      console.error('LanguageContext: Failed to load namespaces:', namespaces, error);
      throw error;
    }
  };

  const registerNamespaces = useCallback(async (namespaces: string[]) => {

    // コンポーネント駆動型システムに登録
    i18nRegisterNamespaces(namespaces);

    // 既に初期化済みの場合は即座に処理
    if (i18n.isInitialized) {
      const currentLoaded = getLoadedNamespaces();
      const filteredNamespaces = namespaces.filter(ns => !currentLoaded.includes(ns));

      if (filteredNamespaces.length > 0) {
        await loadNamespaces(filteredNamespaces);
      }
      return;
    }

    // 初期化未完了の場合のみ待機
    const waitForInitialization = () => {
      return new Promise<void>((resolve) => {
        const checkInit = () => {
          if (i18n.isInitialized) {
            i18n.off('initialized', checkInit);
            resolve();
          }
        };

        i18n.on('initialized', checkInit);

        // 3秒でタイムアウト
        setTimeout(() => {
          i18n.off('initialized', checkInit);
          resolve();
        }, 3000);
      });
    };

    // 初期化完了を待ってからロード処理
    await waitForInitialization();

    // 初期化後のロード処理
    const currentLoaded = getLoadedNamespaces();
    const filteredNamespaces = namespaces.filter(ns => !currentLoaded.includes(ns));

    if (filteredNamespaces.length > 0) {
      await loadNamespaces(filteredNamespaces);
    }
  }, []);

  // 多言語テキスト取得ヘルパー（汎用）
  const getLocalizedText = useCallback(<T extends Record<string, string | undefined>>(
    multiLangObj: T | undefined,
    fallback?: string
  ): string => {
    if (!multiLangObj) {
      if (fallback !== undefined) return fallback;
      throw new Error('Multilingual text does not exist');
    }
    const text = multiLangObj[currentLanguage as keyof T];
    if (text) return text;
    if (multiLangObj.ja) return multiLangObj.ja;
    if (fallback !== undefined) return fallback;
    throw new Error(`No valid value in multilingual text: ${JSON.stringify(multiLangObj)}`);
  }, [currentLanguage]);

  /**
   * 多言語テキスト取得ヘルパー（string型とRecord型両対応）
   * バックエンドAPIがstring型とMultilingualJSON型を混在して返すケースに対応
   * @param value - string型またはRecord<string, string>型の多言語データ
   * @param fallback - 値が存在しない場合の代替値（省略時はError throw）
   * @returns 現在の言語のテキスト
   * @throws 値が存在せずfallbackも未指定の場合
   */
  const getLocalizedString = useCallback((
    value: string | Record<string, string | undefined> | null | undefined,
    fallback?: string
  ): string => {
    if (typeof value === 'string') return value;
    if (typeof value === 'object' && value !== null) {
      const text = value[currentLanguage as keyof typeof value] || value.ja;
      if (text) return text;
      if (fallback !== undefined) return fallback;
      throw new Error(`No valid value in multilingual text: ${JSON.stringify(value)}`);
    }
    if (fallback !== undefined) return fallback;
    throw new Error('Multilingual text does not exist');
  }, [currentLanguage]);

  // ロケールコード取得ヘルパー（date-fns等のロケール指定に使用）
  const getLocaleCode = useCallback((): string => {
    switch (currentLanguage) {
      case 'ko':
        return 'ko-KR';
      case 'zh':
        return 'zh-CN';
      case 'en':
        return 'en-US';
      default:
        return 'ja-JP';
    }
  }, [currentLanguage]);

  // 動的namespace解決付きt関数
  const t = React.useCallback((key: string, options?: any): string => {
    try {
      // プレフィックス解決を最優先
      let resolvedNamespace = 'common';

      if (typeof window !== 'undefined') {
        const currentRegistered = getRegisteredNamespaces();

        // 1. プレフィックス解決（最優先）
        // common.key または errors.key 形式をサポート
        if (key.startsWith('common.')) {
          resolvedNamespace = 'common';
        } else if (key.startsWith('errors.')) {
          resolvedNamespace = 'errors';
        } else if (key.startsWith('layout.')) {
          const layoutNamespaces = currentRegistered.filter(ns => ns.endsWith('Layout'));
          resolvedNamespace = layoutNamespaces[0] || 'common';
        } else {
          // 2. プレフィックスがない場合のみURLベースのnamespace解決
          // ページ専用namespaceリスト（common/Layout/errors以外）
          const pageNamespaces = currentRegistered.filter(ns =>
            ns !== 'common' && ns !== 'errors' && !ns.endsWith('Layout')
          );
          resolvedNamespace = pageNamespaces[0];
        }
      }

      // namespaceプレフィックスをキーから除去
      let translationKey = key;
      if (key.startsWith('common.')) {
        translationKey = key.substring(7); // 'common.' を除去
      }
      // errors.* は除去しない（YAML内にerrorsルートキーがあるため）

      // 初期化状態に関わらず常に翻訳を試行
      const result = i18n.t(translationKey, {
        ...options,
        ns: resolvedNamespace,
        nsSeparator: false, // namespace区切り文字無効化
        keySeparator: '.' // キー区切り文字は維持
      });

      // 翻訳キーがi18nリソース内に実在するか（空文字翻訳を誤って失敗判定しないため）
      const keyExists = i18n.exists(translationKey, {
        ...options,
        ns: resolvedNamespace,
        nsSeparator: false,
        keySeparator: '.'
      });

      // 翻訳成功の判定条件
      // 1. 結果が有効な文字列である（undefined、nullは除外）
      // 2. キーが実在する、またはキーと異なる結果が返される
      // 3. namespace付きエラー形式でない
      // 4. i18nextのデフォルトエラー形式でない
      const isTranslationSuccessful = (
        result !== undefined &&
        result !== null &&
        typeof result === 'string' &&
        (keyExists || result !== key) &&
        !result.startsWith(resolvedNamespace + ':') &&
        !result.startsWith('key not found') &&
        result !== 'undefined' // 文字列"undefined"も除外
      );

      if (isTranslationSuccessful) {
        return result;
      }

      return key;
    } catch (error) {
      console.error('Translation error:', error);
      return key;
    }
  }, []);

  // 初期化時の言語設定はi18n.tsx内で完了しているため、このuseEffectは不要
  // URLパラメータまたはブラウザ言語の検出はinitializeI18n内で実施済み

  // AuthContext方式: useMemoでProvider valueを安定化
  // getLoadedNamespaces()/getRegisteredNamespaces()が毎回新しい配列を生成するため、
  // updateTrigger依存でキャッシュし、参照の安定性を確保
  const loadedNamespaces = useMemo(() => getLoadedNamespaces(), [updateTrigger]);
  const registeredNamespaces = useMemo(() => getRegisteredNamespaces(), [updateTrigger]);

  const value = useMemo(() => ({
    currentLanguage,
    changeLanguage,
    isReady: isI18nReady,
    t,
    registerNamespaces,
    loadedNamespaces,
    registeredNamespaces,
    getLocalizedText,
    getLocalizedString,
    getLocaleCode,
  }), [currentLanguage, changeLanguage, isI18nReady, t, registerNamespaces, loadedNamespaces, registeredNamespaces, getLocalizedText, getLocalizedString, getLocaleCode]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

// 簡素化されたhook関数
export const useTranslation = (namespaces: string[] = []) => {
  const { registerNamespaces, t } = useLanguage();

  // namespaceを自動登録
  React.useEffect(() => {
    if (namespaces.length > 0) {
      registerNamespaces(namespaces);
    }
  }, [namespaces, registerNamespaces]);

  return { t };
};
